// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, verifySession, updateUserActivity, signOut as authSignOut } from '@/lib/auth';
import { getSessionToken, clearSessionToken } from '@/lib/sessionStorage';

interface AuthContextType {
  user: Omit<User, 'password_hash'> | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Omit<User, 'password_hash'> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from session token
  const loadUser = async () => {
    try {
      // FIREFOX FIX: Check sessionStorage FIRST (survives privacy mode)
      const oauthSuccess = sessionStorage.getItem('whipt_oauth_success');
      const oauthToken = sessionStorage.getItem('whipt_oauth_token');
      const oauthUser = sessionStorage.getItem('whipt_oauth_user');
      
      if (oauthSuccess === 'true' && oauthToken && oauthUser) {
        console.log('✅ [AuthContext] Found OAuth session in sessionStorage (Firefox fix)');
        
        // Move to localStorage for persistence
        localStorage.setItem('whipt_session_token', oauthToken);
        localStorage.setItem('whipt_user', oauthUser);
        
        // Clear sessionStorage flags
        sessionStorage.removeItem('whipt_oauth_success');
        sessionStorage.removeItem('whipt_oauth_token');
        sessionStorage.removeItem('whipt_oauth_user');
        
        const userData = JSON.parse(oauthUser);
        setUser(userData);
        setIsLoading(false);
        return;
      }
      
      // Check Supabase session
      const { supabase } = await import('@/lib/supabase');
      const { data: { session: supabaseSession } } = await supabase.auth.getSession();
      
      if (supabaseSession?.user) {
        console.log('✅ [AuthContext] Found Supabase OAuth session:', supabaseSession.user.email);
        
        // Store the session token for future use
        localStorage.setItem('whipt_session_token', supabaseSession.access_token);
        
        const userData = {
          id: supabaseSession.user.id,
          email: supabaseSession.user.email!,
          name: supabaseSession.user.user_metadata.full_name || supabaseSession.user.user_metadata.name || 'User',
          created_at: new Date().toISOString(),
          role: 'user' as const,
          show_online_status: true,
          last_active_at: new Date().toISOString()
        };
        
        localStorage.setItem('whipt_user', JSON.stringify(userData));
        setUser(userData);
        setIsLoading(false);
        return;
      }
      
      // Fall back to local session token
      const token = getSessionToken();
      const cachedUser = localStorage.getItem('whipt_user');
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Developer bypass - skip session verification
      if (token === 'dev_bypass_token' || localStorage.getItem('whipt_dev_mode') === 'true') {
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
        }
        setIsLoading(false);
        return;
      }

      const userData = await verifySession(token);
      if (!userData) {
        console.warn('[Auth] verifySession returned null for token, attempting Supabase fallback');
      }
      if (userData) {
        // 🔒 SECURITY: Check if cached user matches session user
        if (cachedUser) {
          const parsedCache = JSON.parse(cachedUser);
          if (parsedCache.id !== userData.id) {
            console.error('[Auth] ⚠️ Session mismatch detected!');
            console.error('[Auth] Cached user:', parsedCache.email, 'ID:', parsedCache.id);
            console.error('[Auth] Session user:', userData.email, 'ID:', userData.id);
            console.error('[Auth] Clearing mismatched session...');
            
            // Clear everything and force re-login
            localStorage.removeItem('whipt_session_token');
            localStorage.removeItem('whipt_user');
            setUser(null);
            setIsLoading(false);
            
            alert('Session conflict detected! Please sign in again.');
            window.location.href = '/sign-in';
            return;
          }
        }
        
        const { password_hash, ...userWithoutPassword } = userData;
        
        // Load fresh profile data from database (avatar, color)
        try {
          const { turso } = await import('@/database/client');
          const profileResult = await turso.execute({
            sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'profile' LIMIT 1",
            args: [userData.id]
          });
          
          if (profileResult.rows && profileResult.rows.length > 0) {
            const profileData = JSON.parse(profileResult.rows[0].data_value as string);
            userWithoutPassword.avatar = profileData.avatar || userWithoutPassword.avatar;
            userWithoutPassword.profile_color = profileData.profileColor || userWithoutPassword.profile_color;
            console.log('✅ Loaded profile data from database on mount');
          }
        } catch (dbError) {
          console.error('[Auth] Failed to load profile data:', dbError);
        }
        
        setUser(userWithoutPassword);
        
        // Update localStorage with fresh data
        localStorage.setItem('whipt_user', JSON.stringify(userWithoutPassword));
        
        // Update activity
        await updateUserActivity(userData.id);
        
        // 🚀 AUTO-MIGRATION: Migrate user data from localStorage to database
        try {
          const { migrateUserData } = await import('@/lib/migration');
          console.log('[Auth] 🔄 Starting auto-migration for user:', userData.id);
          await migrateUserData(userData.id);
          console.log('[Auth] ✅ Auto-migration completed');
        } catch (migrationError) {
          console.error('[Auth] ⚠️ Migration failed (non-critical):', migrationError);
          // Don't block sign-in if migration fails
        }
      } else {
        // Invalid/expired session
        localStorage.removeItem('whipt_session_token');
        localStorage.removeItem('whipt_user');
      }
    } catch (error) {
      console.error('[Auth] Failed to load user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update user activity every minute
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        updateUserActivity(user.id);
      }, 60 * 1000); // 1 minute

      return () => clearInterval(interval);
    }
  }, [user]);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  // 🔒 Listen for storage changes from other tabs (detect account switches)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only care about user changes, not token refreshes
      if (e.key === 'whipt_user') {
        
        // Check if the new session is for a different user
        if (e.newValue && user) {
          try {
            const newUser = JSON.parse(e.newValue);
            if (newUser.id !== user.id) {
              console.error('[Auth] ⚠️ Different user detected in another tab!');
              alert(`Account switched to ${newUser.name}. Reloading...`);
              window.location.reload();
            } else {
              // Same user, just ignore - don't reload
              console.log('[Auth] ✅ Same user in another tab, no action needed');
            }
          } catch (err) {
            console.error('[Auth] Failed to parse new user data:', err);
          }
        } else if (!e.newValue && user) {
          // Session was cleared
          console.warn('[Auth] Session cleared in another tab. Signing out...');
          setUser(null);
          window.location.href = '/sign-in';
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  const signOut = async () => {
    const token = localStorage.getItem('whipt_session_token');
    if (token) {
      await authSignOut(token);
    }
    
    // Sign out from Supabase (for OAuth users)
    try {
      const { supabase } = await import('@/lib/supabase');
      await supabase.auth.signOut();
    } catch (err) {
      console.log('Supabase sign-out skipped:', err);
    }
    
    // Use persistent storage utility to clear token from all locations
    clearSessionToken();
    localStorage.removeItem('whipt_user');
    localStorage.removeItem('whipt_dev_mode');
    localStorage.removeItem('whipt_bypass_token');
    // DON'T clear organizer_mode - keep preference for next sign-in
    // localStorage.removeItem('whipt_organizer_mode');
    setUser(null);
    
    // Redirect to logout page
    window.location.href = '/logout';
  };

  const refreshUser = async () => {
    console.log('[Auth] refreshUser() called, current user:', user ? user.email : 'NONE');
    
    // If no user in context, try to load from session
    if (!user) {
      console.log('[Auth] No user in context, checking session...');
      const token = localStorage.getItem('whipt_session_token');
      if (!token) {
        console.log('[Auth] No session token found');
        return;
      }
      
      console.log('[Auth] Found session token, verifying...');
      const { verifySession } = await import('@/lib/auth');
      const userData = await verifySession(token);
      
      if (userData) {
        console.log('[Auth] ✅ Session verified! Setting user:', userData.email);
        const { password_hash, ...userWithoutPassword } = userData;
        setUser(userWithoutPassword);
        localStorage.setItem('whipt_user', JSON.stringify(userWithoutPassword));
        return;
      } else {
        console.log('[Auth] ❌ Session verification failed');
        return;
      }
    }
    
    // Reload user data from database, not just session
    if (user) {
      try {
        // 2-second timeout for database refresh (fall back to cached data)
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Refresh timeout')), 2000)
        );
        
        const refreshPromise = (async () => {
          const { turso } = await import('@/database/client');
          
          // Get fresh user data from database
          const userResult = await turso.execute({
            sql: 'SELECT * FROM users WHERE id = ? LIMIT 1',
            args: [user.id]
          });
          
          if (userResult.rows && userResult.rows.length > 0) {
            const userRow = userResult.rows[0];
            const { password_hash, ...userWithoutPassword } = userRow;
            
            // Get profile preferences (avatar, color)
            const profileResult = await turso.execute({
              sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'preferences' AND data_key = 'profile' LIMIT 1",
              args: [user.id]
            });
            
            let profileData: any = {};
            if (profileResult.rows && profileResult.rows.length > 0) {
              profileData = JSON.parse(profileResult.rows[0].data_value as string);
            }
            
            // Merge database data with profile preferences
            const freshUser = {
              ...userWithoutPassword,
              avatar: profileData.avatar || userWithoutPassword.avatar,
              profile_color: profileData.profileColor || userWithoutPassword.profile_color,
              _avatarTimestamp: Date.now() // Cache buster for avatar images
            };
            
            // Update state and localStorage
            setUser(freshUser as any);
            localStorage.setItem('whipt_user', JSON.stringify(freshUser));
            
            console.log('✅ User data refreshed from database', freshUser.id, 'avatar:', freshUser.avatar ? 'present' : 'none');
          }
        })();
        
        await Promise.race([refreshPromise, timeoutPromise]);
      } catch (error) {
        console.warn('[Auth] Failed to refresh user from DB (using cached):', error instanceof Error ? error.message : 'unknown');
        // Keep existing user data from localStorage - don't clear session
      }
    } else {
      // No user loaded, do full load
      await loadUser();
    }
  };

  // Enforce re-login after inactivity
  useEffect(() => {
    if (!user) return;

    const devBypass = localStorage.getItem('whipt_dev_mode') === 'true' || localStorage.getItem('whipt_session_token') === 'dev_bypass_token';
    if (devBypass) return; // don't force logout in dev bypass

    const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
    let timeoutId: number | undefined;
    let signedOut = false;

    const triggerSignOut = async () => {
      if (signedOut) return;
      signedOut = true;
      console.warn('[Auth] Signing out due to inactivity');
      await signOut();
    };

    const resetTimer = () => {
      if (signedOut) return;
      if (timeoutId) clearTimeout(timeoutId);
      localStorage.setItem('whipt_last_activity', Date.now().toString());
      timeoutId = window.setTimeout(triggerSignOut, INACTIVITY_LIMIT_MS);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timeoutId) clearTimeout(timeoutId);
      } else {
        resetTimer();
      }
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'touchstart'];
    activityEvents.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);

    // Start timer only when visible
    if (document.visibilityState === 'visible') resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach((event) => window.removeEventListener(event, resetTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        signOut,
        refreshUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
